"use client";

import React from "react";
import { cn } from "@/lib/utils";

interface Ticket {
    id: string;
    title: string;
    assignee: {
        name: string;
        initials: string;
        color: string;
    };
    type: "feature" | "bug" | "task";
}

const inProgressTickets: Ticket[] = [
    {
        id: "EXP-104",
        title: "Integrate Stripe Payment Gateway",
        assignee: { name: "Alice", initials: "AL", color: "bg-red-500" },
        type: "feature",
    },
    {
        id: "EXP-105",
        title: "Fix Mobile Menu Z-Index Issue",
        assignee: { name: "Bob", initials: "BO", color: "bg-blue-500" },
        type: "bug",
    },
    {
        id: "EXP-108",
        title: "Optimize Database Queries for User Feed",
        assignee: { name: "Charlie", initials: "CH", color: "bg-green-500" },
        type: "task",
    },
    {
        id: "EXP-112",
        title: "Refactor Notification Component",
        assignee: { name: "David", initials: "DA", color: "bg-yellow-500" },
        type: "task",
    },
    {
        id: "EXP-115",
        title: "Implement Dark Mode Toggle",
        assignee: { name: "Eve", initials: "EV", color: "bg-purple-500" },
        type: "feature",
    },
    {
        id: "EXP-118",
        title: "Update API Documentation",
        assignee: { name: "Frank", initials: "FR", color: "bg-orange-500" },
        type: "task",
    },
];

const todoTickets: Ticket[] = [
    {
        id: "EXP-120",
        title: "Design New Landing Page",
        assignee: { name: "Unassigned", initials: "?", color: "bg-gray-300" },
        type: "feature",
    },
];

const doneTickets: Ticket[] = [
    {
        id: "EXP-101",
        title: "Setup Project Repository",
        assignee: { name: "Alice", initials: "AL", color: "bg-red-500" },
        type: "task",
    }
];

function TicketCard({ ticket }: { ticket: Ticket }) {
    return (
        <div className="bg-card border rounded-md p-3 shadow-sm space-y-2 hover:shadow-md transition-shadow cursor-default">
            <div className="flex justify-between items-start gap-2">
                <span className="text-xs font-mono text-muted-foreground">{ticket.id}</span>
                <div className={cn("h-2 w-2 rounded-full shrink-0",
                    ticket.type === "feature" && "bg-blue-400",
                    ticket.type === "bug" && "bg-red-400",
                    ticket.type === "task" && "bg-gray-400"
                )} />
            </div>
            <p className="text-sm font-medium leading-tight">{ticket.title}</p>
            <div className="flex justify-end pt-1">
                <div className={cn("h-6 w-6 rounded-full overflow-hidden ring-2 ring-background", ticket.assignee.color)}>
                    <img
                        src={`https://i.pravatar.cc/64?u=${ticket.assignee.name}`}
                        alt={ticket.assignee.name}
                        className="h-full w-full object-cover"
                    />
                </div>
            </div>
        </div>
    );
}

export function SprintBoard() {
    return (
        <div className="w-full bg-muted/20 border rounded-xl overflow-hidden shadow-inner p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-full">
                {/* To Do Column */}
                <div className="bg-muted/30 rounded-lg p-3 flex flex-col gap-3">
                    <div className="flex items-center justify-between uppercase text-xs font-bold text-muted-foreground tracking-wider px-1">
                        <span>To Do</span>
                        <span className="bg-muted-foreground/20 px-2 py-0.5 rounded-full text-[10px]">{todoTickets.length}</span>
                    </div>
                    {todoTickets.map(t => <TicketCard key={t.id} ticket={t} />)}
                    <div className="border-2 border-dashed border-muted-foreground/10 rounded-md p-4 flex items-center justify-center text-muted-foreground/40 text-xs">
                        Empty Slot
                    </div>
                </div>

                {/* In Progress Column */}
                <div className="bg-muted/40 rounded-lg p-3 flex flex-col gap-3 min-h-[300px] border-t-4 border-blue-400/50">
                    <div className="flex items-center justify-between uppercase text-xs font-bold text-blue-600 dark:text-blue-400 tracking-wider px-1">
                        <span>In Progress</span>
                        <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full text-[10px]">{inProgressTickets.length}</span>
                    </div>
                    {inProgressTickets.map(t => <TicketCard key={t.id} ticket={t} />)}
                </div>

                {/* Done Column */}
                <div className="bg-muted/30 rounded-lg p-3 flex flex-col gap-3">
                    <div className="flex items-center justify-between uppercase text-xs font-bold text-muted-foreground tracking-wider px-1">
                        <span>Done</span>
                        <span className="bg-muted-foreground/20 px-2 py-0.5 rounded-full text-[10px]">{doneTickets.length}</span>
                    </div>
                    {doneTickets.map(t => <TicketCard key={t.id} ticket={t} />)}
                    <div className="border-2 border-dashed border-muted-foreground/10 rounded-md p-4 flex items-center justify-center text-muted-foreground/40 text-xs">
                        Empty Slot
                    </div>
                </div>
            </div>
        </div>
    );
}
